import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
} from "@mui/material";
import { Close, Download, TrendingUp, TrendingDown, KeyboardArrowDown, KeyboardArrowUp, Fullscreen, FullscreenExit } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportBarChart from "components/reports/charts/ReportBarChart";

/**
 * DrillDownDrawer - Shows detailed breakdowns when clicking chart elements
 * 
 * @param {boolean} open - Whether drawer is open
 * @param {Function} onClose - Close handler
 * @param {string} title - Drawer title
 * @param {string} subtitle - Optional subtitle
 * @param {string} description - Optional detailed description/explanation
 * @param {Array} data - Data to display in table
 * @param {Array} columns - Column configuration
 * @param {Object} summary - Optional summary metrics to show at top
 * @param {Function} onExport - Optional export handler
 * @param {boolean} useBarChart - Use bar chart instead of pie charts for expandable rows
 */
const DrillDownDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  description,
  data = [],
  columns = [],
  summary,
  onExport,
  useBarChart = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hasInitializedFullscreen, setHasInitializedFullscreen] = useState(false);
  const [hasInitializedPreferences, setHasInitializedPreferences] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(700);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRafRef = useRef(null);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(700);

  const MIN_DRAWER_WIDTH = 560;
  const MIN_VIEWPORT_GUTTER = 80;
  const DRAWER_WIDTH_STORAGE_KEY = 'REPORTS_DRILLDOWN_DRAWER_WIDTH';
  const DRAWER_FULLSCREEN_STORAGE_KEY = 'REPORTS_DRILLDOWN_DRAWER_FULLSCREEN';
  const desktopWidth = `${Math.max(MIN_DRAWER_WIDTH, Math.min(drawerWidth, window.innerWidth - MIN_VIEWPORT_GUTTER))}px`;

  useEffect(() => {
    if (!open) {
      setHasInitializedPreferences(false);
      return;
    }

    const savedFullscreenRaw = localStorage.getItem(DRAWER_FULLSCREEN_STORAGE_KEY);
    const savedFullscreen = savedFullscreenRaw === 'true';

    const savedWidthRaw = localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY);
    const savedWidth = savedWidthRaw ? parseInt(savedWidthRaw, 10) : null;
    const defaultWidth = savedWidth && !Number.isNaN(savedWidth)
      ? savedWidth
      : (window.innerWidth >= 1200 ? 700 : window.innerWidth * 0.9);
    const maxWidth = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - MIN_VIEWPORT_GUTTER);
    const clampedWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(defaultWidth, maxWidth));

    setDrawerWidth(clampedWidth);
    setIsFullScreen(savedFullscreen);
    setHasInitializedFullscreen(true);
    setHasInitializedPreferences(true);
  }, [open]);

  useEffect(() => {
    if (!open || isMobile || isFullScreen || !hasInitializedPreferences) return;
    const maxWidth = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - MIN_VIEWPORT_GUTTER);
    const clampedWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(drawerWidth, maxWidth));
    if (clampedWidth !== drawerWidth) {
      setDrawerWidth(clampedWidth);
    }
  }, [open, isMobile, isFullScreen, drawerWidth, hasInitializedPreferences]);

  useEffect(() => {
    if (!open || isMobile || isFullScreen || !hasInitializedPreferences) return;
    localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(Math.round(drawerWidth)));
  }, [drawerWidth, isMobile, isFullScreen, open, hasInitializedPreferences]);

  useEffect(() => {
    if (!open || !hasInitializedFullscreen || !hasInitializedPreferences) return;
    localStorage.setItem(DRAWER_FULLSCREEN_STORAGE_KEY, String(isFullScreen));
  }, [isFullScreen, open, hasInitializedFullscreen, hasInitializedPreferences]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event) => {
      if (resizeRafRef.current) return;
      resizeRafRef.current = requestAnimationFrame(() => {
        const maxWidth = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - MIN_VIEWPORT_GUTTER);
        const deltaX = resizeStartXRef.current - event.clientX;
        const nextWidth = resizeStartWidthRef.current + deltaX;
        setDrawerWidth(Math.max(MIN_DRAWER_WIDTH, Math.min(nextWidth, maxWidth)));
        resizeRafRef.current = null;
      });
    };

    const handleMouseUp = () => {
      if (!isMobile && !isFullScreen) {
        localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(Math.round(drawerWidth)));
      }
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.document.body.style.userSelect = 'none';
    window.document.body.style.cursor = 'col-resize';

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.userSelect = '';
      window.document.body.style.cursor = '';
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [isResizing, isMobile, isFullScreen, drawerWidth]);

  const handleSort = (columnField) => {
    if (orderBy !== columnField) {
      // First click: sort ascending
      setOrder('asc');
      setOrderBy(columnField);
    } else if (order === 'asc') {
      // Second click: sort descending
      setOrder('desc');
    } else {
      // Third click: clear sorting
      setOrderBy('');
      setOrder('asc');
    }
  };

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Check if row has service breakdown data (for expandable feature)
  const hasServiceBreakdown = (row) => {
    // Check for expense categories (for bar chart)
    if (useBarChart) {
      return row.categories !== undefined && Array.isArray(row.categories) && row.categories.length > 0;
    }
    
    // Check for revenue breakdown
    const hasRevenueBreakdown = row.translationRevenue !== undefined || 
                                 row.interpretationRevenue !== undefined || 
                                 row.otherServicesRevenue !== undefined;
    // Check for cost breakdown
    const hasCostBreakdown = row.translationCosts !== undefined || 
                              row.interpretationCosts !== undefined || 
                              row.otherServicesCosts !== undefined;
    // Check for gross profit breakdown
    const hasGrossProfitBreakdown = row.translationGrossProfit !== undefined || 
                                     row.interpretationGrossProfit !== undefined || 
                                     row.otherServicesGrossProfit !== undefined;
    return hasRevenueBreakdown || hasCostBreakdown || hasGrossProfitBreakdown;
  };

  // Sort data based on current sort state
  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle null/undefined
      if (aValue == null) return order === 'asc' ? 1 : -1;
      if (bValue == null) return order === 'asc' ? -1 : 1;

      // Special handling for month columns (format: "Mar 2025", "Apr 2025", etc.)
      if (orderBy.toLowerCase() === 'month' || (typeof aValue === 'string' && /^[A-Za-z]{3}\s\d{4}$/.test(aValue))) {
        const monthMap = {
          jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
          jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
        };
        
        const parseMonthYear = (str) => {
          const parts = String(str).split(' ');
          if (parts.length !== 2) return new Date(0);
          const monthName = parts[0].toLowerCase();
          const year = parseInt(parts[1]);
          const monthIndex = monthMap[monthName];
          return new Date(year, monthIndex || 0);
        };
        
        const aDate = parseMonthYear(aValue);
        const bDate = parseMonthYear(bValue);
        
        return order === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings (case-insensitive)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  const handleExport = () => {
    const exportData = sortedData.length > 0 ? sortedData : data;
    
    if (onExport) {
      onExport(exportData);
    } else {
      // Default CSV export
      const headers = columns.map(col => col.headerName).join(",");
      const rows = exportData.map(row => 
        columns.map(col => {
          const value = row[col.field];
          return typeof value === "string" && value.includes(",") ? `"${value}"` : value;
        }).join(",")
      ).join("\n");
      
      const csv = `${headers}\n${rows}`;
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s+/g, "_")}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isFullScreen ? "100%" : (isMobile ? "100%" : desktopWidth),
          minWidth: isFullScreen ? "100%" : (isMobile ? "100%" : `${MIN_DRAWER_WIDTH}px`),
          maxWidth: isFullScreen ? '100%' : `calc(100vw - ${MIN_VIEWPORT_GUTTER}px)`,
          bgcolor: theme.palette.background.default,
          position: 'relative',
          right: 0,
          left: 'auto !important',
          marginLeft: 'auto',
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {!isFullScreen && (
          <Box
            onMouseDown={(event) => {
              const maxWidth = Math.max(MIN_DRAWER_WIDTH, window.innerWidth - MIN_VIEWPORT_GUTTER);
              resizeStartXRef.current = event.clientX;
              resizeStartWidthRef.current = Math.max(MIN_DRAWER_WIDTH, Math.min(drawerWidth, maxWidth));
              setIsResizing(true);
            }}
            sx={{
              position: 'absolute',
              top: 0,
              left: -4,
              width: 8,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 1300,
              '&:hover': {
                bgcolor: theme.palette.primary.main,
                opacity: 0.15,
              },
            }}
          />
        )}
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.alt,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                onClick={() => setIsFullScreen(!isFullScreen)} 
                sx={{ mt: -1 }}
                title={isFullScreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
              <IconButton onClick={onClose} sx={{ mt: -1, mr: -1 }}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* Description */}
          {description && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                maxHeight: '40vh',
                overflow: 'auto',
              }}
            >
              {typeof description === 'string' ? (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                    color: "text.secondary",
                  }}
                >
                  {description}
                </Typography>
              ) : (
                description
              )}
            </Box>
          )}

          {/* Summary Metrics */}
          {summary && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {summary.metrics?.map((metric, index) => (
                <Grid key={index} size={{ xs: 6, sm: 4 }}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: theme.shadows[4],
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {metric.label}
                      </Typography>
                      <Typography variant="h6" fontWeight="600" sx={{ color: metric.color, mt: 1 }}>
                        {metric.value}
                      </Typography>
                      {metric.trend !== undefined && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                          {metric.trend >= 0 ? (
                            <TrendingUp sx={{ fontSize: 16, color: "success.main" }} />
                          ) : (
                            <TrendingDown sx={{ fontSize: 16, color: "error.main" }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{ color: metric.trend >= 0 ? "success.main" : "error.main" }}
                          >
                            {metric.trend >= 0 ? "+" : ""}
                            {metric.trend}%
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          {data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
                borderRadius: "12px",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50, fontWeight: 700, bgcolor: theme.palette.background.default }} />
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          fontWeight: 700,
                          bgcolor: theme.palette.background.default,
                          width: column.width || "auto",
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === column.field}
                          direction={orderBy === column.field ? order : 'asc'}
                          onClick={() => handleSort(column.field)}
                          sx={{
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                            '&.Mui-active': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {column.headerName}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => {
                    const isExpanded = expandedRow === index;
                    const showExpandButton = hasServiceBreakdown(row);
                    
                    return (
                      <React.Fragment key={row.id || index}>
                        <TableRow
                          sx={{
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                            },
                            cursor: showExpandButton ? "pointer" : "default",
                          }}
                        >
                          <TableCell sx={{ width: 50 }}>
                            {showExpandButton && (
                              <IconButton
                                size="small"
                                onClick={() => handleRowClick(index)}
                              >
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>
                            )}
                          </TableCell>
                          {columns.map((column) => (
                            <TableCell 
                              key={column.field}
                              onClick={showExpandButton ? () => handleRowClick(index) : undefined}
                            >
                              {column.renderCell
                                ? column.renderCell(row)
                                : row[column.field]}
                            </TableCell>
                          ))}
                        </TableRow>
                        {showExpandButton && (
                          <TableRow>
                            <TableCell colSpan={columns.length + 1} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 3, px: 2 }}>
                                  {useBarChart ? (
                                    // Bar chart for Operating Expenses
                                    (() => {
                                      const categories = row.categories || [];
                                      const barChartData = categories.map((cat, idx) => ({
                                        category: cat.name,
                                        amount: cat.amount,
                                        expenses: cat.expenses || [],
                                        id: idx,
                                      }));
                                      
                                      return (
                                        <Box>
                                          <ReportBarChart
                                            title={`${row.month} - Expenses by Category`}
                                            data={barChartData}
                                            xAxisKey="category"
                                            tooltipKey="category"
                                            series={[
                                              {
                                                dataKey: "amount",
                                                label: "Amount",
                                                color: theme.palette.error.main,
                                              },
                                            ]}
                                            height={400}
                                            showInPaper={false}
                                            valueFormatter={(value) => `$${value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                            yAxisLabel="Amount ($)"
                                            showLegend={false}
                                          />
                                          
                                          {/* All Expenses Table */}
                                          <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 2 }}>
                                              All Expenses - Detailed List
                                            </Typography>
                                            <TableContainer component={Paper} variant="outlined">
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow sx={{ bgcolor: theme.palette.background.alt }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Sub Category</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Amount</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {categories.flatMap((cat) => 
                                                    cat.expenses?.map((expense, expIdx) => (
                                                      <TableRow 
                                                        key={`${cat.name}-${expIdx}`}
                                                        sx={{ 
                                                          '&:hover': { bgcolor: theme.palette.action.hover },
                                                          '&:last-child td': { border: 0 },
                                                        }}
                                                      >
                                                        <TableCell>
                                                          <Typography variant="body2">
                                                            {expense.description || "No description"}
                                                          </Typography>
                                                          {expense.referenceNumber && expense.referenceNumber !== "N/A" && (
                                                            <Typography variant="caption" color="text.secondary">
                                                              Ref: {expense.referenceNumber}
                                                            </Typography>
                                                          )}
                                                        </TableCell>
                                                        <TableCell>
                                                          <Typography variant="body2">
                                                            {expense.category || cat.name}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Typography variant="body2" color={expense.subCategory === "N/A" ? "text.secondary" : "text.primary"}>
                                                            {expense.subCategory || "N/A"}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Typography variant="body2">
                                                            {expense.date ? new Date(expense.date).toLocaleDateString(undefined, { 
                                                              year: 'numeric', 
                                                              month: 'short', 
                                                              day: 'numeric' 
                                                            }) : "N/A"}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Typography variant="body2">
                                                            {expense.paymentMethod || "N/A"}
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'right' }}>
                                                          <Typography variant="body2" fontWeight="600" color="error.main">
                                                            ${expense.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                          </Typography>
                                                        </TableCell>
                                                      </TableRow>
                                                    )) || []
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </TableContainer>
                                          </Box>
                                        </Box>
                                      );
                                    })()
                                  ) : (
                                    // Pie charts for Revenue/Cost/Gross Profit
                                    (() => {
                                      // Determine breakdown type and data
                                      const hasRevenue = row.translationRevenue !== undefined;
                                      const hasCosts = row.translationCosts !== undefined;
                                      const hasGrossProfit = row.translationGrossProfit !== undefined;
                                      
                                      let translation, interpretation, otherServices, total, chartTitle;
                                      
                                      if (hasGrossProfit) {
                                        translation = row.translationGrossProfit || 0;
                                        interpretation = row.interpretationGrossProfit || 0;
                                        otherServices = row.otherServicesGrossProfit || 0;
                                        total = translation + interpretation + otherServices;
                                        chartTitle = "Gross Profit by Service Type";
                                      } else if (hasRevenue) {
                                        translation = row.translationRevenue || 0;
                                        interpretation = row.interpretationRevenue || 0;
                                        otherServices = row.otherServicesRevenue || 0;
                                        total = translation + interpretation + otherServices;
                                        chartTitle = "Revenue by Service Type";
                                      } else if (hasCosts) {
                                        translation = row.translationCosts || 0;
                                        interpretation = row.interpretationCosts || 0;
                                        otherServices = row.otherServicesCosts || 0;
                                        total = translation + interpretation + otherServices;
                                        chartTitle = "Costs by Service Type";
                                      }
                                      
                                      const translationCount = row.translationServicesCount || 0;
                                      const interpretationCount = row.interpretationServicesCount || 0;
                                      const otherServicesCountValue = row.otherServicesCount || 0;
                                      const totalCount = translationCount + interpretationCount + otherServicesCountValue;
                                      
                                      return (
                                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', flexDirection: { xs: 'column', md: 'row' } }}>
                                          <Box sx={{ flex: 1 }}>
                                            <ReportPieChart
                                              title={chartTitle}
                                              data={[
                                                {
                                                  id: 1,
                                                  value: interpretation,
                                                  label: "Interpretation",
                                                },
                                                {
                                                  id: 2,
                                                  value: translation,
                                                  label: "Translation",
                                                },
                                                {
                                                  id: 3,
                                                  value: otherServices,
                                                  label: "Other Services",
                                                },
                                              ]}
                                              height={300}
                                              showInPaper={false}
                                              showLabels={false}
                                              showLegend={true}
                                              legendPosition="bottom"
                                              valueFormatter={(value) => {
                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${percentage}%)`;
                                              }}
                                            />
                                          </Box>
                                          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                                          <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
                                          <Box sx={{ flex: 1 }}>
                                            <ReportPieChart
                                              title="Services Count by Type"
                                              data={[
                                                {
                                                  id: 1,
                                                  value: interpretationCount,
                                                  label: "Interpretation",
                                                },
                                                {
                                                  id: 2,
                                                  value: translationCount,
                                                  label: "Translation",
                                                },
                                                {
                                                  id: 3,
                                                  value: otherServicesCountValue,
                                                  label: "Other Services",
                                                },
                                              ]}
                                              height={300}
                                              showInPaper={false}
                                              showLabels={false}
                                              showLegend={true}
                                              legendPosition="bottom"
                                              valueFormatter={(value) => {
                                                const percentage = totalCount > 0 ? ((value / totalCount) * 100).toFixed(1) : 0;
                                                return `${value} services (${percentage}%)`;
                                              }}
                                            />
                                          </Box>
                                        </Box>
                                      );
                                    })()
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.alt,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {data.length} {data.length === 1 ? "record" : "records"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={data.length === 0}
            >
              Export CSV
            </Button>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DrillDownDrawer;
